"use client";

import React, { useState } from "react";
import Image from "next/image";
import { PLACE_HOLDER_IMAGE } from "@/src/shared/constants/app";
import { isOptimizableImage } from "@/src/shared/utils/optimizableImage";

interface ICardImageProps {
  src: string;
  alt: string;
  priority?: boolean;
  /** The photo's displayed width per breakpoint, so the browser picks a small file. */
  sizes?: string;
  className?: string;
  index?: number;
  isSeeMoreButton?: boolean;
}

const CardImage: React.FC<ICardImageProps> = ({
  src,
  alt,
  priority,
  sizes = "(max-width: 768px) 100vw, 283px",
  className = "",
  isSeeMoreButton,
}) => {
  // Load the real image straight away; a flat grey slot shows until it lands (a pulse
  // per card kept dozens of animations running while the page scrolled).
  // A failed URL falls back to the placeholder once (it used to bounce between the two).
  const [status, setStatus] = useState<"loading" | "loaded" | "error">(
    src ? "loading" : "error",
  );
  const imageSrc = status === "error" ? PLACE_HOLDER_IMAGE : src;
  return (
    <div className={"relative rounded-2xl bg-gray-100 " + className}>
      <Image
        src={imageSrc}
        alt={alt}
        fill
        className={`object-cover rounded-2xl transition-opacity duration-300 ${
          status === "loading" ? "opacity-0" : "opacity-100"
        }`}
        style={{ zIndex: 1 }}
        sizes={sizes}
        priority={priority}
        // Resized by Next to the card's size (sharp); the originals are full-size uploads
        // that took long enough to download and decode to leave blank cards on a fast scroll.
        unoptimized={!isOptimizableImage(imageSrc)}
        onLoad={() => setStatus((s) => (s === "loading" ? "loaded" : s))}
        onError={() => setStatus("error")}
      />
      {isSeeMoreButton && (
        <button
          type="button"
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 px-6 py-2 bg-white/30 text-black text-xs font-noraml rounded-xl transition hover:bg-white/50 focus:outline-none shadow-lg backdrop-blur-md"
          style={{ zIndex: 2, backdropFilter: "blur(8px)" }}
        >
          Click to view all images
        </button>
      )}
    </div>
  );
};

export default CardImage;
